import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  Typography
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import Layout from "../../components/Layout/Layout";
import apiEndpoint from "../../config/environment";
import {
  isFetchBaseQueryError,
  useCreateBannerMutation,
} from "../../services/admin";
import UploadBox from "./Components/UploadBox";

const options = [
  {
    title: "Admin Login",
    value: "ADMIN_LOGIN",
  },
  {
    title: "Coach/Client Login",
    value: "COACH_CLIENT_LOGIN",
  },
  {
    title: "Client Header",
    value: "CLIENT_HEADER",
  },
  {
    title: "Coach Header",
    value: "COACH_HEADER",
  },
];

interface Type {
  title: string;
  value: string;
}

interface IFormInputs {
  type: string; // Changed from Type to string
  image: string;
}

interface ImageState {
  name: string;
  file: File | null;
  isUploading: boolean;
  url: string;
}

const schema = yup.object().shape({
  type: yup.string().required("Required"),
  image: yup.string().required("Required"),
});

const UploadChangeImage = () => {
  const [imageType, setImageType] = useState<Type | null>(null);
  const [image, setImage] = useState<ImageState>({
    name: "",
    file: null,
    isUploading: false,
    url: "",
  });

  const [createBanner, { isLoading }] = useCreateBannerMutation();
  const navigate = useNavigate();

  const {
    handleSubmit,
    register,
    setValue,
    control,
    trigger,
    watch,
    formState: { errors },
  } = useForm<IFormInputs>({
    resolver: yupResolver(schema),
  });

  // Watch the type field to enable/disable file upload
  const selectedType = watch("type");

  const handleChange = (event: any) => {
    const targetValue = event.target.value;
    setImageType(targetValue);
    setValue("type", targetValue.value); // Set only the string value
    trigger("type");
  };

  const onSubmit: SubmitHandler<IFormInputs> = async (data: IFormInputs) => {
    const payload = {
      contentType: data.type, // This is now a string
      imageUrl: data.image,
    };
    try {
      await createBanner(payload)
        .unwrap()
        .then((res) => {
          toast.success(res?.message);
        });
      navigate("/change-image");
    } catch (error) {
      if (isFetchBaseQueryError(error)) {
        toast.error(error.data?.message);
      }
    }
  };

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    const file = e.target.files[0];
    const previewUrl = URL.createObjectURL(file);

    setImage({
      name: file.name,
      file: file,
      isUploading: true,
      url: previewUrl,
    });

    const formData = new FormData();
    formData.append("image", file);

    const promise = axios
      .request({
        method: "POST",
        url: `${apiEndpoint}/upload-one`,
        data: formData,
      })
      .then((res) => {
        setImage(prev => ({
          ...prev,
          isUploading: false,
          url: res.data,
        }));
        setValue("image", res.data);
        trigger("image");
      })
      .catch((error) => {
        console.error("Upload error:", error);
        setImage(prev => ({
          ...prev,
          isUploading: false,
        }));
      });

    await toast.promise(promise, {
      pending: "Uploading Image",
      success: "Uploaded Image",
      error: "Error Uploading Image",
    });
  };

  useEffect(() => {
    return () => {
      if (image.url && image.url.startsWith('blob:')) {
        URL.revokeObjectURL(image.url);
      }
    };
  }, [image.url]);

  return (
    <Layout>
      <div className="pt-[6.636rem] pb-[4.633rem] px-[16rem]">
        <h4 className="text-[#333333] text-[3.2rem] font-bold mb-[1.9rem]">
          Upload Image For
        </h4>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormControl
            sx={{
              marginTop: "2rem",
              marginBottom: "2rem",
              width: "50rem",
              "& .MuiInputBase-root": {
                height: "5.25rem",
                boxShadow: "0px 1px 4px rgb(0 0 0 / 10%)",
                borderRadius: "0.76rem",
              },
            }}
          >
            <Typography
              sx={{ fontSize: "2rem", fontWeight: 400, mb: "1.2rem" }}
            >
              Select
            </Typography>
            <Box sx={{ mt: 0 }}>
              <Controller
                control={control}
                name="type"
                render={({ field: { onChange, ...restField } }) => (
                  <Select
                    sx={{
                      maxWidth: "50rem",
                      width: "100%",
                      fontSize: "2rem",
                      fontWeight: 400,
                      borderColor: "#2165A2",
                      borderRadius: "2rem",
                      py: "0rem",
                      px: "1rem",
                      "& .MuiSelect-icon": {
                        width: "3rem",
                        height: "3rem",
                        top: "calc(50% - 1.5rem)",
                      },
                      "& .MuiMenuItem-root": {
                        fontSize: "1.8rem",
                      },
                      "& .MuiSelect-select": {
                        py: "1.8rem",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          "& .MuiMenuItem-root": {
                            fontSize: "1.8rem",
                            maxWidth: "50rem !important",
                          },
                        },
                      },
                    }}
                    fullWidth
                    {...restField}
                    placeholder="Select"
                    onChange={(event: any) => {
                      onChange(event);
                      handleChange(event);
                    }}
                    renderValue={(selected: any) => {
                      if (!selected || selected?.length === 0) {
                        return <span>Select One</span>;
                      }
                      return <span>{selected?.title}</span>;
                    }}
                  >
                    {options?.map((item: any, index: any) => (
                      <MenuItem key={index} value={item}>
                        {item.title}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors?.type && (
                <p
                  style={{
                    fontSize: "10px",
                    color: "red",
                    marginTop: "10px",
                    marginBottom: "10px",
                  }}
                >
                  {errors?.type?.message}
                </p>
              )}
            </Box>
          </FormControl>

          <Box sx={{ display: "flex", gap: "6rem", alignItems: "flex-start" }}>
            <Box sx={{
              width: "50rem",
              minHeight: "30rem"
            }}>
              <UploadBox
                name="image"
                accept="image/*"
                uploadTitle="Upload Image"
                type="photo"
                fileName={image.name || "Image12.png"}
                progress={100}
                status="completed"
                isUploading={image.isUploading}
                showImages
                handleFileChange={handleUploadImage}
                register={register}
                disabled={!selectedType}
              />
              {errors?.image?.message ? (
                <p className="text-red-500 py-[10px]">
                  {errors.image.message}
                </p>
              ) : null}
            </Box>

            {image.url && (
              <Box sx={{
                width: "50rem",
                display: "flex",
                flexDirection: "column"
              }}>
                <Typography sx={{
                  fontSize: "1.6rem",
                  fontWeight: 500,
                  mb: "1rem",
                  height: "2.4rem"
                }}>
                  Preview
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    height: "30rem",
                    border: "2px dashed #e0e0e0",
                    borderRadius: "0.76rem",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#fafafa",
                    position: "relative",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    }
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      width: "auto",
                      height: "auto",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <img
                      src={image.url}
                      alt="Upload preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        display: "block"
                      }}
                      onLoad={(e) => {
                        const img = e.target as HTMLImageElement;
                        console.log('Image loaded:', img.naturalWidth, 'x', img.naturalHeight);
                      }}
                    />
                  </Box>

                  {image.isUploading && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: "1rem"
                      }}
                    >
                      <Box
                        sx={{
                          width: "4rem",
                          height: "4rem",
                          border: "3px solid #f3f3f3",
                          borderTop: "3px solid #2165A2",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite"
                        }}
                      />
                      <Typography sx={{
                        fontSize: "1.4rem",
                        color: "#2165A2",
                        fontWeight: 500
                      }}>
                        Uploading...
                      </Typography>
                    </Box>
                  )}
                </Box>

                {image.name && (
                  <Typography sx={{
                    fontSize: "1.2rem",
                    color: "text.secondary",
                    mt: 1,
                    textAlign: "center"
                  }}>
                    {image.name}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <div className="mt-[8rem]">
            <button
              disabled={isLoading || image?.isUploading || !selectedType || !image.url}
              type="submit"
              className="bg-[#2165A2] rounded-[0.481505rem] w-[18.402rem] h-[4.508rem] text-white text-[1.80564rem] font-bold disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {(isLoading || image?.isUploading) ? "Loading..." : "Upload Image"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default UploadChangeImage;